import React, { useCallback, useContext, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  BillingAdminApiError,
  TBillingAdminAuth,
  TAdminClinicSignupClinic,
  getBillingAdminClinicSignups,
} from "../../../services/api-billing-admin";
import { SessionContext } from "../../../contexts";
import { ADMIN_TOKENS } from "../../../styles/adminTokens";
import {
  Input,
  PrimaryButton,
  SecondaryButton,
  TitleText,
} from "../../common/native";
import AdminAnalyticsTabs from "./AdminAnalyticsTabs";

const TOKENS = ADMIN_TOKENS;
const DATE_PRESET_OPTIONS = [
  "Last 7 days",
  "Last 30 days",
  "Last 60 days",
  "Last 90 days",
  "Last 6 months",
  "Last 12 months",
];

const formatDateInput = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getLastSevenDaysRange = () => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  return {
    startDate: formatDateInput(start),
    endDate: formatDateInput(end),
  };
};

const getPresetDateRange = (preset: string) => {
  const today = new Date();
  const endDate = formatDateInput(today);

  switch (preset) {
    case "Last 7 days": {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      return { startDate: formatDateInput(start), endDate };
    }
    case "Last 30 days": {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      return { startDate: formatDateInput(start), endDate };
    }
    case "Last 60 days": {
      const start = new Date(today);
      start.setDate(start.getDate() - 59);
      return { startDate: formatDateInput(start), endDate };
    }
    case "Last 90 days": {
      const start = new Date(today);
      start.setDate(start.getDate() - 89);
      return { startDate: formatDateInput(start), endDate };
    }
    case "Last 6 months": {
      const start = new Date(today);
      start.setMonth(start.getMonth() - 6);
      return { startDate: formatDateInput(start), endDate };
    }
    case "Last 12 months": {
      const start = new Date(today);
      start.setMonth(start.getMonth() - 12);
      return { startDate: formatDateInput(start), endDate };
    }
    default:
      return { startDate: "", endDate: "" };
  }
};

const formatDateTime = (dateString?: string | null) => {
  if (!dateString) return { local: "-", utc: "-" };
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return { local: dateString, utc: "-" };
  return {
    local: date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }),
    utc: date.toUTCString(),
  };
};

const WEB_DATE_INPUT_STYLE: React.CSSProperties = {
  height: TOKENS.size.inputHeight,
  borderRadius: TOKENS.radius.md,
  border: `${TOKENS.borderWidth.sm}px solid ${TOKENS.color.borderInput}`,
  padding: `${TOKENS.spacing.sm}px ${TOKENS.spacing.md}px`,
  fontFamily: TOKENS.font.medium,
  fontSize: TOKENS.fontSize.md,
  color: TOKENS.color.textPrimary,
  backgroundColor: TOKENS.color.surface,
};

const DateInput: React.FC<{
  label: string;
  value: string;
  onChange: (value: string) => void;
}> = ({ label, value, onChange }) => {
  if (Platform.OS === "web") {
    return (
      <View style={styles.dateField}>
        <Text style={styles.dateFieldLabel}>{label}</Text>
        <input
          type="date"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          style={WEB_DATE_INPUT_STYLE}
        />
      </View>
    );
  }

  return (
    <View style={styles.dateField}>
      <Input
        name={label}
        placeholder="YYYY-MM-DD"
        value={value}
        onChangeText={onChange}
      />
    </View>
  );
};

const AdminClinicSignupsPage: React.FC = () => {
  const sessionContext = useContext(SessionContext);
  const session = sessionContext?.session;
  const defaultRange = useMemo(getLastSevenDaysRange, []);

  const [activeAuthMode, setActiveAuthMode] = useState<"apiKey" | "jwt">(
    "apiKey",
  );
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [authSecret, setAuthSecret] = useState<TBillingAdminAuth | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [datePreset, setDatePreset] = useState("Last 7 days");
  const [isQuickRangeModalVisible, setIsQuickRangeModalVisible] =
    useState(false);
  const [limit, setLimit] = useState("50");
  const [includeDemo, setIncludeDemo] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");

  const [clinics, setClinics] = useState<TAdminClinicSignupClinic[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [totalClinicsMatching, setTotalClinicsMatching] = useState(0);

  const activeAuthBadge = authSecret
    ? authSecret.type === "apiKey"
      ? "Billing key"
      : "Admin JWT"
    : "Not connected";

  const fetchClinicSignupsByAuth = useCallback(
    async (auth: TBillingAdminAuth, cursor?: string | null, append = false) => {
      const parsedLimit = Number(limit);
      const resolvedLimit = Number.isFinite(parsedLimit)
        ? Math.min(Math.max(parsedLimit, 1), 1000)
        : 50;

      setIsLoading(true);
      setErrorMessage(null);
      try {
        const response = await getBillingAdminClinicSignups(auth, {
          startDate,
          endDate,
          limit: resolvedLimit,
          cursor: cursor || undefined,
          includeDemo,
        });

        setClinics((previous) =>
          append ? [...previous, ...response.clinics] : response.clinics,
        );
        setNextCursor(response.next_cursor || null);
        setTotalClinicsMatching(response.total_clinics_matching);
      } catch (error) {
        if (error instanceof BillingAdminApiError) {
          if (error.status === 401 || error.status === 403) {
            setAuthSecret(null);
          }
          setErrorMessage(error.message);
        } else if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage("Failed to load new user signups.");
        }
      } finally {
        setIsLoading(false);
      }
    },
    [endDate, includeDemo, limit, startDate],
  );

  const fetchClinicSignups = useCallback(
    async (cursor?: string | null, append = false) => {
      if (!authSecret) {
        setErrorMessage("Connect with a billing admin key or admin JWT first.");
        return;
      }
      await fetchClinicSignupsByAuth(authSecret, cursor, append);
    },
    [authSecret, fetchClinicSignupsByAuth],
  );

  const handleConnectWithApiKey = () => {
    const trimmedKey = apiKeyInput.trim();
    if (!trimmedKey) {
      setErrorMessage("Enter your billing admin key to connect.");
      return;
    }
    setErrorMessage(null);
    setClinics([]);
    setNextCursor(null);
    setTotalClinicsMatching(0);
    const nextAuth = { type: "apiKey", secret: trimmedKey } as const;
    setAuthSecret(nextAuth);
    void fetchClinicSignupsByAuth(nextAuth, null, false);
  };

  const handleConnectWithJwt = () => {
    const token = session?.accessToken;
    if (!token) {
      setErrorMessage("Sign in to the admin portal to use the JWT flow.");
      return;
    }
    setErrorMessage(null);
    setClinics([]);
    setNextCursor(null);
    setTotalClinicsMatching(0);
    const nextAuth = { type: "jwt", secret: token } as const;
    setAuthSecret(nextAuth);
    void fetchClinicSignupsByAuth(nextAuth, null, false);
  };

  const handleReset = () => {
    const range = getLastSevenDaysRange();
    setApiKeyInput("");
    setAuthSecret(null);
    setErrorMessage(null);
    setClinics([]);
    setNextCursor(null);
    setTotalClinicsMatching(0);
    setLimit("50");
    setIncludeDemo(false);
    setSearchFilter("");
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setDatePreset("Last 7 days");
    setIsQuickRangeModalVisible(false);
  };

  const filteredClinics = useMemo(() => {
    const search = searchFilter.trim().toLowerCase();
    if (!search) return clinics;

    return clinics.filter((clinic) => {
      const clinicMatch = [clinic.clinic_name, clinic.clinic_uuid]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(search);

      if (clinicMatch) return true;

      return clinic.users.some((user) => {
        const userHaystack = [user.supabase_uuid, user.email, user.role]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return userHaystack.includes(search);
      });
    });
  }, [clinics, searchFilter]);

  return (
    <ScrollView style={styles.page} contentContainerStyle={styles.pageContent}>
      <View style={styles.headerRow}>
        <View style={styles.headerTextGroup}>
          <TitleText text="Admin New Users" />
          <Text style={styles.subtitle}>
            Review clinics and signup users with emails when available. Defaults
            to the last 7 days.
          </Text>
        </View>
        <View style={styles.badgeWrapper}>
          <Text style={styles.authBadgeLabel}>Auth mode</Text>
          <Text style={[styles.authBadge, !authSecret && styles.authBadgeIdle]}>
            {activeAuthBadge}
          </Text>
        </View>
      </View>

      <AdminAnalyticsTabs activeTab="newUsers" />

      <View style={styles.card}>
        <Text style={styles.cardTitle}>Connection</Text>
        <Text style={styles.cardSubtitle}>
          Authenticate with either a billing admin key or an admin bearer
          session.
        </Text>

        <View style={styles.authToggleRow}>
          <SecondaryButton
            text="Billing Admin Key"
            onPress={() => setActiveAuthMode("apiKey")}
            selected={activeAuthMode === "apiKey"}
            style={styles.authToggleButton}
          />
          <SecondaryButton
            text="Admin JWT"
            onPress={() => setActiveAuthMode("jwt")}
            selected={activeAuthMode === "jwt"}
            style={styles.authToggleButton}
          />
        </View>

        {activeAuthMode === "apiKey" ? (
          <View style={styles.authContent}>
            <Input
              name="Billing Admin Key"
              placeholder="Paste admin key"
              value={apiKeyInput}
              onChangeText={setApiKeyInput}
              autoCapitalize="none"
              secureTextEntry
            />
            <PrimaryButton
              text="Connect with key"
              onPress={handleConnectWithApiKey}
            />
          </View>
        ) : (
          <View style={styles.authContent}>
            <Text style={styles.jwtHint}>
              {session?.accessToken
                ? `Using current session token (${session.userEmail}).`
                : "Sign in to an admin account to use JWT auth."}
            </Text>
            <PrimaryButton
              text="Use current admin session"
              onPress={handleConnectWithJwt}
              disabled={!session?.accessToken}
            />
          </View>
        )}

        <SecondaryButton
          text="Reset credentials and filters"
          onPress={handleReset}
        />
      </View>

      {authSecret && (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>Filters</Text>
          <Text style={styles.cardSubtitle}>
            Date range defaults to the last 7 days.
          </Text>

          <View style={styles.filtersRow}>
            <DateInput
              label="Start date"
              value={startDate}
              onChange={(value) => {
                setStartDate(value);
                setDatePreset("");
              }}
            />
            <DateInput
              label="End date"
              value={endDate}
              onChange={(value) => {
                setEndDate(value);
                setDatePreset("");
              }}
            />
            <View style={styles.dateField}>
              <Text style={styles.dateFieldLabel}>Quick range</Text>
              <Pressable
                style={styles.quickRangeButton}
                onPress={() => setIsQuickRangeModalVisible(true)}
              >
                <Text
                  style={[
                    styles.quickRangeButtonText,
                    !datePreset && styles.quickRangeButtonTextPlaceholder,
                  ]}
                >
                  {datePreset || "Custom range"}
                </Text>
              </Pressable>
            </View>
            <View style={styles.dateField}>
              <Input
                name="Limit"
                placeholder="50"
                value={limit}
                onChangeText={setLimit}
                keyboardType="numeric"
              />
            </View>
          </View>

          <View style={styles.filtersRow}>
            <View style={styles.searchField}>
              <Input
                name="Search"
                placeholder="Clinic, email, role, UUID"
                value={searchFilter}
                onChangeText={setSearchFilter}
                autoCapitalize="none"
              />
            </View>
            <Pressable
              onPress={() => setIncludeDemo((value) => !value)}
              style={styles.checkboxRow}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: includeDemo }}
              accessibilityLabel="Include demo clinics"
            >
              <View
                style={[styles.checkbox, includeDemo && styles.checkboxChecked]}
              >
                {includeDemo ? (
                  <Text style={styles.checkboxCheck}>v</Text>
                ) : null}
              </View>
              <Text style={styles.checkboxLabel}>Include demo clinics</Text>
            </Pressable>
          </View>

          <View style={styles.buttonRow}>
            <PrimaryButton
              text="Apply filters"
              onPress={() => fetchClinicSignups(null, false)}
            />
            <SecondaryButton
              text="Reset to last 7 days"
              onPress={() => {
                const range = getLastSevenDaysRange();
                setStartDate(range.startDate);
                setEndDate(range.endDate);
                setDatePreset("Last 7 days");
              }}
            />
          </View>
        </View>
      )}

      <Modal
        visible={isQuickRangeModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setIsQuickRangeModalVisible(false)}
      >
        <Pressable
          style={styles.quickRangeModalOverlay}
          onPress={() => setIsQuickRangeModalVisible(false)}
        />
        <View style={styles.quickRangeModalCard}>
          <Text style={styles.quickRangeModalTitle}>Select quick range</Text>
          <ScrollView style={styles.quickRangeOptionsList}>
            {DATE_PRESET_OPTIONS.map((preset) => {
              const isSelected = preset === datePreset;
              return (
                <Pressable
                  key={preset}
                  style={[
                    styles.quickRangeOptionButton,
                    isSelected && styles.quickRangeOptionButtonSelected,
                  ]}
                  onPress={() => {
                    const { startDate: nextStartDate, endDate: nextEndDate } =
                      getPresetDateRange(preset);
                    setStartDate(nextStartDate);
                    setEndDate(nextEndDate);
                    setDatePreset(preset);
                    setIsQuickRangeModalVisible(false);
                  }}
                >
                  <Text
                    style={[
                      styles.quickRangeOptionText,
                      isSelected && styles.quickRangeOptionTextSelected,
                    ]}
                  >
                    {preset}
                  </Text>
                </Pressable>
              );
            })}
          </ScrollView>
          <SecondaryButton
            text="Close"
            onPress={() => setIsQuickRangeModalVisible(false)}
          />
        </View>
      </Modal>

      {errorMessage ? (
        <View style={styles.errorCard}>
          <Text style={styles.errorTitle}>Unable to load new users</Text>
          <Text style={styles.errorMessage}>{errorMessage}</Text>
        </View>
      ) : null}

      {isLoading ? (
        <View style={styles.loaderRow}>
          <ActivityIndicator size="small" color={TOKENS.color.accentDark} />
          <Text style={styles.loaderText}>Loading clinic signups...</Text>
        </View>
      ) : null}

      {authSecret && !isLoading ? (
        filteredClinics.length ? (
          <View style={styles.list}>
            <View style={styles.periodCard}>
              <Text style={styles.periodText}>
                Showing {filteredClinics.length} clinics in view. Total
                matching: {totalClinicsMatching}.
              </Text>
            </View>

            {filteredClinics.map((clinic) => {
              const clinicCreated = formatDateTime(clinic.clinic_created_at);
              return (
                <View key={clinic.clinic_uuid} style={styles.clinicCard}>
                  <View style={styles.clinicHeader}>
                    <View style={styles.clinicHeaderText}>
                      <Text style={styles.clinicTitle}>
                        {clinic.clinic_name || "Unnamed clinic"}
                      </Text>
                      <Text style={styles.clinicMeta}>
                        {clinic.clinic_uuid}
                      </Text>
                      <Text style={styles.clinicMeta}>
                        Created: {clinicCreated.local}
                      </Text>
                    </View>
                    <View style={styles.userCountBadge}>
                      <Text style={styles.userCountText}>
                        {clinic.users.length} new users
                      </Text>
                    </View>
                  </View>

                  {!clinic.users.length ? (
                    <Text style={styles.emptyText}>
                      No signup users returned.
                    </Text>
                  ) : (
                    <View style={styles.userList}>
                      {clinic.users.map((user) => {
                        const userCreated = formatDateTime(
                          user.signup_created_at,
                        );
                        return (
                          <View
                            key={`${clinic.clinic_uuid}-${user.clinic_user_id}`}
                            style={styles.userRow}
                          >
                            <Text style={styles.userPrimary}>
                              {user.email || "No matching email"}
                            </Text>
                            <Text style={styles.userSecondary}>
                              Role: {user.role}
                            </Text>
                            <Text style={styles.userSecondary}>
                              Supabase UUID: {user.supabase_uuid}
                            </Text>
                            <Text style={styles.userSecondary}>
                              Signup created: {userCreated.local}
                            </Text>
                          </View>
                        );
                      })}
                    </View>
                  )}
                </View>
              );
            })}

            {nextCursor ? (
              <SecondaryButton
                text="Load more"
                onPress={() => fetchClinicSignups(nextCursor, true)}
                style={styles.loadMoreButton}
              />
            ) : null}
          </View>
        ) : (
          <View style={styles.emptyCard}>
            <Text style={styles.emptyTitle}>No new users found</Text>
            <Text style={styles.emptyText}>
              Update the date range or include demo clinics to broaden results.
            </Text>
          </View>
        )
      ) : null}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  page: {
    backgroundColor: TOKENS.color.background,
    flex: 1,
  },
  pageContent: {
    padding: TOKENS.spacing.xxl,
    gap: TOKENS.spacing.lg,
  },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: TOKENS.spacing.lg,
    flexWrap: "wrap",
  },
  headerTextGroup: {
    gap: TOKENS.spacing.sm,
    flexShrink: 1,
  },
  subtitle: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textSecondary,
    maxWidth: TOKENS.size.contentMaxWidth,
  },
  badgeWrapper: {
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.lg,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.md,
    minWidth: TOKENS.size.badgeMinWidth,
    gap: TOKENS.spacing.xs,
  },
  authBadgeLabel: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textTertiary,
  },
  authBadge: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.accentDark,
  },
  authBadgeIdle: {
    color: TOKENS.color.idle,
  },
  card: {
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.xl,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.lg,
    gap: TOKENS.spacing.md,
  },
  cardTitle: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.xl,
    color: TOKENS.color.textPrimary,
  },
  cardSubtitle: {
    fontFamily: TOKENS.font.regular,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textMuted,
  },
  authToggleRow: {
    flexDirection: "row",
    gap: TOKENS.spacing.sm,
    flexWrap: "wrap",
  },
  authToggleButton: {
    minWidth: TOKENS.size.authButtonMinWidth,
  },
  authContent: {
    gap: TOKENS.spacing.md,
  },
  jwtHint: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSecondary,
  },
  filtersRow: {
    flexDirection: "row",
    gap: TOKENS.spacing.md,
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
  dateField: {
    flex: 1,
    minWidth: TOKENS.size.dateFieldMinWidth,
    gap: TOKENS.spacing.xs,
  },
  quickRangeButton: {
    minHeight: TOKENS.size.inputHeight,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.borderInput,
    borderRadius: TOKENS.radius.md,
    paddingHorizontal: TOKENS.spacing.md,
    justifyContent: "center",
    backgroundColor: TOKENS.color.surface,
  },
  quickRangeButtonText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textPrimary,
  },
  quickRangeButtonTextPlaceholder: {
    color: TOKENS.color.idle,
  },
  quickRangeModalOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: TOKENS.color.textPrimary,
    opacity: 0.2,
  },
  quickRangeModalCard: {
    position: "absolute",
    top: "20%",
    left: TOKENS.spacing.lg,
    right: TOKENS.spacing.lg,
    maxHeight: "60%",
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.lg,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.md,
    gap: TOKENS.spacing.sm,
  },
  quickRangeModalTitle: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.lg,
    color: TOKENS.color.textPrimary,
  },
  quickRangeOptionsList: {
    maxHeight: 280,
  },
  quickRangeOptionButton: {
    minHeight: 44,
    borderRadius: TOKENS.radius.md,
    paddingHorizontal: TOKENS.spacing.md,
    paddingVertical: TOKENS.spacing.sm,
    justifyContent: "center",
  },
  quickRangeOptionButtonSelected: {
    backgroundColor: TOKENS.color.surfaceMuted,
  },
  quickRangeOptionText: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textPrimary,
  },
  quickRangeOptionTextSelected: {
    color: TOKENS.color.accentDark,
  },
  dateFieldLabel: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
  },
  searchField: {
    flex: 1,
    minWidth: TOKENS.size.dateFieldMinWidth,
  },
  checkboxRow: {
    minHeight: 44,
    flexDirection: "row",
    alignItems: "center",
    gap: TOKENS.spacing.sm,
    paddingHorizontal: TOKENS.spacing.sm,
    paddingVertical: TOKENS.spacing.sm,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: TOKENS.radius.sm,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.borderStrong,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: TOKENS.color.surface,
  },
  checkboxChecked: {
    backgroundColor: TOKENS.color.accent,
    borderColor: TOKENS.color.accent,
  },
  checkboxCheck: {
    color: TOKENS.color.surface,
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.sm,
    lineHeight: TOKENS.lineHeight.sm,
  },
  checkboxLabel: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
  },
  buttonRow: {
    flexDirection: "row",
    gap: TOKENS.spacing.sm,
    flexWrap: "wrap",
  },
  loaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: TOKENS.spacing.sm,
    paddingHorizontal: TOKENS.spacing.xs,
  },
  loaderText: {
    fontFamily: TOKENS.font.medium,
    color: TOKENS.color.textSecondary,
  },
  errorCard: {
    backgroundColor: TOKENS.color.errorSurface,
    borderColor: TOKENS.color.errorBorder,
    borderWidth: TOKENS.borderWidth.sm,
    borderRadius: TOKENS.radius.lg,
    padding: TOKENS.spacing.md,
    gap: TOKENS.spacing.xs,
  },
  errorTitle: {
    fontFamily: TOKENS.font.bold,
    color: TOKENS.color.errorText,
    fontSize: TOKENS.fontSize.md,
  },
  errorMessage: {
    fontFamily: TOKENS.font.medium,
    color: TOKENS.color.errorTextStrong,
    fontSize: TOKENS.fontSize.sm,
  },
  periodCard: {
    backgroundColor: TOKENS.color.surfaceAccent,
    borderColor: TOKENS.color.borderAccent,
    borderWidth: TOKENS.borderWidth.sm,
    borderRadius: TOKENS.radius.lg,
    padding: TOKENS.spacing.md,
  },
  periodText: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.accentDark,
  },
  list: {
    gap: TOKENS.spacing.md,
  },
  clinicCard: {
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.lg,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.lg,
    gap: TOKENS.spacing.md,
  },
  clinicHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: TOKENS.spacing.md,
    flexWrap: "wrap",
  },
  clinicHeaderText: {
    gap: TOKENS.spacing.xs,
    flex: 1,
  },
  clinicTitle: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.lg,
    color: TOKENS.color.textPrimary,
  },
  clinicMeta: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSecondary,
  },
  userCountBadge: {
    borderRadius: TOKENS.radius.md,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    backgroundColor: TOKENS.color.surfaceMuted,
    paddingHorizontal: TOKENS.spacing.md,
    paddingVertical: TOKENS.spacing.sm,
    minHeight: 44,
    justifyContent: "center",
  },
  userCountText: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textSecondary,
  },
  userList: {
    gap: TOKENS.spacing.sm,
  },
  userRow: {
    borderRadius: TOKENS.radius.md,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.borderMuted,
    backgroundColor: TOKENS.color.surfaceMuted,
    padding: TOKENS.spacing.md,
    gap: TOKENS.spacing.xs,
  },
  userPrimary: {
    fontFamily: TOKENS.font.semibold,
    fontSize: TOKENS.fontSize.sm,
    color: TOKENS.color.textPrimary,
  },
  userSecondary: {
    fontFamily: TOKENS.font.medium,
    fontSize: TOKENS.fontSize.xs,
    color: TOKENS.color.textSecondary,
  },
  emptyCard: {
    backgroundColor: TOKENS.color.surface,
    borderRadius: TOKENS.radius.lg,
    borderWidth: TOKENS.borderWidth.sm,
    borderColor: TOKENS.color.border,
    padding: TOKENS.spacing.lg,
    gap: TOKENS.spacing.xs,
  },
  emptyTitle: {
    fontFamily: TOKENS.font.bold,
    fontSize: TOKENS.fontSize.md,
    color: TOKENS.color.textPrimary,
  },
  emptyText: {
    fontFamily: TOKENS.font.medium,
    color: TOKENS.color.textMuted,
    fontSize: TOKENS.fontSize.sm,
  },
  loadMoreButton: {
    minWidth: TOKENS.size.authButtonMinWidth,
    alignSelf: "flex-start",
  },
});

export default AdminClinicSignupsPage;
